// The per-item colour opt-in shared by the home and programme grids
// (payload/src/fields/background-color.ts): a background colour the item only
// carries once it opts in, an optional toggle that moves it from the resting
// fill to the hover, and a text colour that either pins black / white or
// leaves the pick to the contrast rule.

export interface ColorableItem {
  useBackgroundColor?: boolean | null
  backgroundColor?: string | null
  useBackgroundColorForHover?: boolean | null
  textColor?: 'auto' | 'white' | 'black' | null
}

// An item carries a colour only once it opts in via useBackgroundColor;
// everything else falls back to the page's own default.
export const chosenBackgroundColor = (item: ColorableItem) =>
  (item.useBackgroundColor && item.backgroundColor) || null

// `auto` leaves the pick to the contrast rule; the other two pin it, for the
// colours where that rule is technically right but reads wrong. The choice
// only goes with a chosen background colour (the admin hides it otherwise), so
// an item on the default colour always follows the contrast rule.
export const forcedTextColor = (item: ColorableItem) => {
  if (!chosenBackgroundColor(item)) return null
  if (item.textColor === 'white') return 'var(--color--white)'
  if (item.textColor === 'black') return 'var(--color--black)'
  return null
}

// A mask item can spend its colour on one state or the other, not both:
// useBackgroundColorForHover moves it off the resting fill (which then sits in
// the page default) and onto the hover. The forced text colour follows it to
// whichever state paints it; the other state keeps the contrast pick.
export const maskRestColors = (item: ColorableItem) => {
  if (item.useBackgroundColorForHover) return { background: null, text: null }
  return { background: chosenBackgroundColor(item), text: forcedTextColor(item) }
}

export const maskHoverColors = (item: ColorableItem) => {
  if (!item.useBackgroundColorForHover) return { background: null, text: null }
  return { background: chosenBackgroundColor(item), text: forcedTextColor(item) }
}
